import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Header } from "../../components/Header";
import Footer from "../../components/Footer/Footer";
import { useIsMobile } from "../../hooks/useIsMobile";
import { checkAuth } from "../../utils/utils";
import { NotFoundPage } from "../NotFoundPage/NotFoundPage";
import Loader from "../../components/Loader/Loader";

import paidIcon from "./image/paid-icon.svg";
import arrowRight from "./image/arrow-right.svg";
import styles from "./PaymentSuccesPage.module.scss";

import { API_BASE } from "config/api";

export const PaymentSuccessPage = () => {
  const API_URL = `${API_BASE}/tickets/order/?payment_key=`;
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { orderId } = useParams<{ orderId: string }>();

  const [isValidOrder, setIsValidOrder] = useState<boolean>(false);
  const [isNotFound, setIsNotFound] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<
    "paid" | "unpaid" | "unknown"
  >("unknown");
  const [orderData, setOrderData] = useState<{
    email?: string;
    event_name?: string;
    ticket_type_name?: string;
    quantity?: number;
    event_starts_at?: string;
    total_price?: number;
    event_id?: number;
  }>({});

  const getAuthToken = (): string | null => {
    const cookies = document.cookie;
    const cookiesArray = cookies.split(";");

    for (const cookie of cookiesArray) {
      const [name, value] = cookie.trim().split("=");

      if (name === "authToken") {
        return value;
      }
    }

    return null;
  };

  const fetchOrderData = useCallback(async () => {
    if (!orderId) {
      setError("Упс, что-то пошло не так!");
      setIsLoading(false);
      return;
    }

    const url = `${API_URL}${orderId}`;
    const token = getAuthToken();

    if (!token) {
      setError("Упс, что-то пошло не так!");
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 404) {
        setIsNotFound(true);
        setIsLoading(false);
        return;
      }

      if (!response.ok) {
        throw new Error("Server error");
      }

      const data = await response.json();
      console.log("API data:", data);

      const ticketset: { ticket_type_name: string; quantity: number }[] =
        Array.isArray(data.ticketset) ? data.ticketset : [];

      const totalQuantity = ticketset.reduce(
        (sum, ticket) => sum + (ticket.quantity || 0),
        0
      );

      const ticketTypes = ticketset
        .map((ticket) => ticket.ticket_type_name)
        .join(", ");

      setOrderData({
        email: data.user_email || data.email,
        event_name: data.event_name,
        ticket_type_name: ticketTypes,
        quantity: totalQuantity,
        event_starts_at: data.event_starts_at,
        total_price: data.total_price,
        event_id: data.event_id,
      });

      setIsValidOrder(true);

      if (data.status === "paid") {
        setPaymentStatus("paid");
      } else {
        setPaymentStatus("unpaid");
      }
    } catch (err: any) {
      console.error("Ошибка:", err?.message);
      setError("Упс, что-то пошло не так!");
    } finally {
      setIsLoading(false);
    }
  }, [orderId, API_URL]);

  useEffect(() => {
    if (!checkAuth()) {
      setError("Упс, что-то пошло не так!");
      setIsLoading(false);
      return;
    }

    fetchOrderData();
  }, [fetchOrderData]);

  const handleGoToEvents = () => {
    navigate("/");
  };

  const handleRetryPayment = () => {
    const eventId = orderData.event_id;

    if (eventId) {
      navigate(`/event/${eventId}`);
    } else {
      navigate("/");
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleString("ru-RU", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatPrice = (price?: number) => {
    if (price === undefined) return "0 ₽";
    return new Intl.NumberFormat("ru-RU", {
      style: "currency",
      currency: "RUB",
      minimumFractionDigits: 0,
    }).format(price);
  };

  const getTicketWord = (quantity?: number) => {
    if (!quantity || quantity === 1) return "Билет";
    return "Билеты";
  };

  // Лоадер
  if (isLoading) {
    return (
      <>
        {!isMobile && <Header />}
        <main className={styles.PaymentSuccessPage}>
          <Loader fullscreen />
        </main>
        {!isMobile && <Footer />}
      </>
    );
  }

  if (isNotFound) {
    return <NotFoundPage />;
  }

  // Общая ошибка
  if (error) {
    return (
      <>
        {!isMobile && <Header />}
        <main>
          <div className={styles.PaymentSuccessPage}>
            <div className={styles.text}>
              <h1 className={styles.title}>{error}</h1>
            </div>
            <button className={styles.btnNormal} onClick={handleGoToEvents}>
              <span>К афише событий</span>
              <img src={arrowRight} alt="Стрелка вправо" />
            </button>
          </div>
        </main>
        {!isMobile && <Footer />}
      </>
    );
  }

  // Оплата не завершена / Оплата не прошла
  if (!isValidOrder || paymentStatus === "unpaid") {
    return (
      <>
        {!isMobile && <Header />}
        <main>
          <div className={styles.PaymentSuccessPage}>
            <div className={styles.text}>
              <h1 className={`${styles.title} ${styles.titlePayment}`}>
                {isMobile ? "Оплата не прошла" : "Оплата не завершена"}
              </h1>
              <p className={styles.subtitle}>
                {isMobile
                  ? "Пожалуйста, попробуйте ещё раз"
                  : "Пожалуйста, попробуйте оплатить ещё раз"}
              </p>
            </div>
            <button
              className={`${styles.btnNormal} ${styles.btnToOrder}`}
              onClick={handleRetryPayment}
            >
              <span>{isMobile ? "Повторить оплату" : "К оплате"}</span>
            </button>
          </div>
        </main>
        {!isMobile && <Footer />}
      </>
    );
  }

  // Успешная оплата
  return (
    <>
      {!isMobile && <Header />}
      <main>
        <div className={styles.PaymentSuccessPage}>
          <img className={styles.icon} src={paidIcon} alt="Успешно" />
          <div className={styles.text}>
            <h1 className={styles.title}>Оплачено!</h1>
            {isMobile ? (
              <div className={styles.infoMobile}>
                <p className={styles.infoTop}>
                  {getTicketWord(orderData.quantity)} отправлен
                  {orderData.quantity === 1 ? "" : "ы"} на
                </p>
                <p className={styles.infoBottom}>{orderData.email}</p>
                <div className={styles.orderDetails}>
                  <p>
                    <strong>Мероприятие:&nbsp;</strong>
                    {orderData.event_name}
                  </p>
                  <p>
                    <strong>Тип билета:&nbsp;</strong>
                    {orderData.ticket_type_name || "—"}
                  </p>
                  <p>
                    <strong>Количество:&nbsp;</strong>
                    {orderData.quantity ?? "—"}
                  </p>
                  <p>
                    <strong>Цена:&nbsp;</strong>
                    {formatPrice(orderData.total_price)}
                  </p>
                  <p>
                    <strong>Начало:&nbsp;</strong>
                    {formatDate(orderData.event_starts_at)}
                  </p>
                </div>
              </div>
            ) : (
              <>
                <p className={styles.info}>
                  {getTicketWord(orderData.quantity)} отправлен
                  {orderData.quantity === 1 ? "" : "ы"} на{" "}
                  <span className={styles.email}>{orderData.email}</span>
                </p>
                <div className={styles.orderDetails}>
                  <p>
                    <strong>Мероприятие:</strong>&nbsp;
                    {orderData.event_name}
                  </p>
                  <p>
                    <strong>Тип билета:</strong>&nbsp;
                    {orderData.ticket_type_name || "—"}
                  </p>
                  <p>
                    <strong>Количество:</strong>&nbsp;
                    {orderData.quantity ?? "—"}
                  </p>
                  <p>
                    <strong>Цена:</strong>&nbsp;
                    {formatPrice(orderData.total_price)}
                  </p>
                  <p>
                    <strong>Начало:</strong>&nbsp;
                    {formatDate(orderData.event_starts_at)}
                  </p>
                </div>
              </>
            )}
          </div>

          <button className={styles.btnNormal} onClick={handleGoToEvents}>
            <span>К афише событий</span>
            <img src={arrowRight} alt="Стрелка вправо" />
          </button>
        </div>
      </main>
      {!isMobile && <Footer />}
    </>
  );
};
